import { type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp, useFetch } from '../store/app';
import { Fade } from './common';
import { Icon } from '../components/Icon';
import { Card, Button, Avatar, StatusBadge, Tag } from '../components/primitives';
import { st } from '../lib/status';
import { av } from '../lib/avatar';
import { can } from '../lib/rbac';
import type { Person, StatusKind } from '../../shared/types';

interface PersonProject { id: number; name: string; status: StatusKind; priority: string; due_date: string; assignment_role: string; client_name: string | null }

export function StaffDetail() {
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const nav = useNavigate();
  const { openDrawer, user } = useApp();
  const { data: person, error } = useFetch<Person>(`/people/${id}`);
  const { data: projects } = useFetch<PersonProject[]>(`/people/${id}/projects`);

  const canEdit = !!user && can(user.role, 'update', 'people');

  if (error) return <BackShell nav={nav}><Empty>Couldn’t load this staff member.</Empty></BackShell>;
  if (!person) return <BackShell nav={nav}><Empty>Loading…</Empty></BackShell>;

  const a = av(person.name, person.avatar_kind);
  const address = [person.addr_line1, person.addr_line2, [person.city, person.state].filter(Boolean).join(', '), person.zip].filter(Boolean).join(' · ');
  const projs = projects || [];

  return (
    <Fade>
      <BackLink nav={nav} />
      <Card style={{ overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '18px 20px', borderBottom: '1px solid var(--border)' }}>
          <Avatar initials={a.in} bg={a.bg} fg={a.fg} size={52} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.01em' }}>{person.name}</span>
              <StatusBadge badge={st(person.type === 'FTE' ? 'fte' : 'contractor')} pill={false} />
              <StatusBadge badge={st(person.status)} />
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 2 }}>{person.role || '—'}</div>
          </div>
          {canEdit && <Button variant="secondary" icon="edit" onClick={() => openDrawer({ type: 'person', mode: 'edit', id })}>Edit</Button>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, padding: '16px 20px' }}>
          {metaCell('Email', person.email || '—')}
          {metaCell('Phone', person.phone || '—')}
          {metaCell('Day rate', person.day_rate || '—')}
          {metaCell('Mailing address', address || '—')}
          {metaCell('Type', person.type)}
          {metaCell('Geo', person.lat != null ? 'Located' : 'Not geocoded')}
        </div>
      </Card>

      <Section title="Skills & tags">
        {person.skills.length === 0 && <Empty>No skills tagged.</Empty>}
        {person.skills.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {person.skills.map((s) => <Tag key={s}>{s}</Tag>)}
          </div>
        )}
      </Section>

      <Section title={`Assignments (${projs.length})`}>
        {projs.length === 0 && <Empty>Not assigned to any projects.</Empty>}
        {projs.map((p) => (
          <div key={p.id} onClick={() => openDrawer({ type: 'project', mode: 'edit', id: p.id })} className="cc-hover-border cc-clickable" style={{ display: 'flex', alignItems: 'center', gap: 11, border: '1px solid var(--border)', borderRadius: 10, padding: 12, background: 'var(--surface)' }}>
            <Icon name="view_kanban" size={20} color="var(--text-3)" />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>{p.name}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{[p.client_name, p.assignment_role, p.due_date ? `due ${p.due_date}` : ''].filter(Boolean).join(' · ') || '—'}</div>
            </div>
            <StatusBadge badge={st(p.status)} />
          </div>
        ))}
      </Section>
    </Fade>
  );
}

function BackLink({ nav }: { nav: ReturnType<typeof useNavigate> }) {
  return (
    <button onClick={() => nav('/people')} className="cc-hover-s2 cc-clickable" style={{ appearance: 'none', border: 0, background: 'transparent', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 600, color: 'var(--text-3)', padding: '4px 8px 4px 4px', borderRadius: 7, marginBottom: 12 }}>
      <Icon name="arrow_back" size={18} />Staff
    </button>
  );
}

function BackShell({ nav, children }: { nav: ReturnType<typeof useNavigate>; children: ReactNode }) {
  return <Fade><BackLink nav={nav} />{children}</Fade>;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</div>
    </div>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <div style={{ fontSize: 12.5, color: 'var(--text-3)', padding: '4px 2px' }}>{children}</div>;
}

function metaCell(label: string, value: ReactNode) {
  return (
    <div key={label}>
      <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 500, wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}
